import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, adminAuthAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Check if user is authenticated on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      console.log('Initializing auth - Token exists:', !!storedToken, 'User exists:', !!storedUser);

      if (storedToken && storedUser) {
        try {
          // For admin panel, only try admin validation
          console.log('Validating admin token...');
          const adminResponse = await adminAuthAPI.validateToken();
          if (adminResponse.data.success) {
            console.log('Admin token valid, setting user');
            setUser(JSON.parse(storedUser));
            setToken(storedToken);
            setIsAdmin(true);
          } else {
            throw new Error('Invalid admin token response');
          }
        } catch (error) {
          console.log('Token validation failed:', error.message);
          // Token is invalid or expired, clear stored data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
          setIsAdmin(false);
        }
      } else {
        console.log('No stored credentials found');
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials, isAdminLogin = false) => {
    try {
      console.log('Attempting login...', isAdminLogin ? 'Admin' : 'Regular');
      const response = isAdminLogin
        ? await adminAuthAPI.signin(credentials)
        : await authAPI.signin(credentials);

      if (response.data.success) {
        const { token: newToken, admin, user: userData } = response.data.data;

        const userInfo = admin || userData;
        console.log('Login successful, storing credentials');
        setUser(userInfo);
        setToken(newToken);
        setIsAdmin(isAdminLogin);

        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(userInfo));

        return { success: true };
      }
    } catch (error) {
      console.log('Login failed:', error.response?.data?.message);
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const signup = async (userData, isAdminSignup = false) => {
    try {
      const response = isAdminSignup
        ? await adminAuthAPI.signup(userData)
        : await authAPI.signup(userData);

      if (response.data.success) {
        const { token: newToken, admin, user: userInfo } = response.data.data;

        const userData = admin || userInfo;
        setUser(userData);
        setToken(newToken);
        setIsAdmin(isAdminSignup);

        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(userData));

        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Signup failed'
      };
    }
  };

  const logout = () => {
    console.log('Logging out user');
    setUser(null);
    setToken(null);
    setIsAdmin(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  // Debug function to check auth state (callable from browser console)
  const debugAuthState = () => {
    console.log('Auth Debug Info:', {
      user: user ? { ...user, password: '[HIDDEN]' } : null,
      token: token ? `${token.substring(0, 20)}...` : null,
      isAdmin,
      loading,
      isAuthenticated: !!user,
      localStorage: {
        token: !!localStorage.getItem('token'),
        user: !!localStorage.getItem('user')
      }
    });
  };

  // Make debug function available globally
  if (typeof window !== 'undefined') {
    window.debugAuthState = debugAuthState;
  }

  const value = {
    user,
    isAdmin,
    token,
    loading,
    login,
    signup,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
